import AdvancedSearchTabular from './tabular';
import AdvancedSearchRangeFields from './range-fields';
import AdvancedSearchAutoComplete from './autocomplete';
import {
    ComponentElements,
    ComponentInjector,
    ComponentObject,
    ComponentSelector,
    ComponentService,
    UIComponent,
    ComponentSelectors,
    Inject,
    BrowserUtils
} from '@atypon/ui-utils';

class Texts extends ComponentObject { }

class ClassList extends ComponentObject { }

class Selectors extends ComponentSelectors {
    searchInFields = new ComponentSelector('.advanced-search__search-in');
    searchInInputs = '.advanced-search__input';
    rangeField = new ComponentSelector('.advanced-search__timeframe');
    submitButton = new ComponentSelector('#advanced-search-btn', 'document');
    autoCompleteFields = new ComponentSelector('.advanced-search__autocomplete');
    advancedFiltersRadioInputs = '.advanced-search__advanced-filters .radio--primary input[type="radio"]';
}

class Elements extends ComponentElements<Selectors> {
    rangeField: HTMLElement;
    searchInFields: HTMLElement;
    submitButton: HTMLButtonElement;
    autoCompleteFields: Array<HTMLElement> = [];
}

interface AdvancedSearch extends ComponentService<Selectors, Elements, ClassList, Texts> { }

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class AdvancedSearch implements UIComponent {

    @Inject()
    protected browserUtils:BrowserUtils;

    /**
    * {AdvancedSearchTabular}
    */
    tabularListener: AdvancedSearchTabular;

    /**
     * {AdvancedSearchRangeFields}
     */
    rangeFieldListener: AdvancedSearchRangeFields;

    /**
     * {AdvancedSearchAutoComplete}
     */
    autoCompleteListener: AdvancedSearchAutoComplete;

    constructor(readonly wrapper: HTMLElement) { }

    initialize(): void {
        this.elements.initialize(this.wrapper);
        this.addEventListeners();
    }


    addEventListeners(): void {
        this.initTabular();
        this.initRange();
        this.initAutoComplete();
        this.setSubmitButtonState();
        this.setResetButtonListener();
        this.selectAdvancedFiltersRadioInput();
    }

    /**
     * creates instance AdvancedSearchTabular to handle the Tabular Fields
     */
    initTabular() {
        this.tabularListener = new AdvancedSearchTabular(this.elements.searchInFields, this.setSubmitButtonState.bind(this));
        this.tabularListener.initialize();

    }

    /**
     * creates instance AdvancedSearchRangeFields to handle the state if enable/disabled of range filters
     */
    initRange() {
        this.rangeFieldListener = new AdvancedSearchRangeFields(this.elements.rangeField,  this.setSubmitButtonState.bind(this));
        this.rangeFieldListener.initialize();
    }

    /**
     * creates instance AdvancedSearchAutoComplete to handle the autocompletion this contains the logic of autocomplete
     */
    initAutoComplete() {
        if (!this.browserUtils.isIE) {
            this.autoCompleteListener = new AdvancedSearchAutoComplete(this.elements.autoCompleteFields, this.setSubmitButtonState.bind(this));
            this.autoCompleteListener.initialize();
        }
    }

    /**
     * enables/disables the submit button depending on the form content
     */
    setSubmitButtonState(event?:CustomEvent) {
        const state = (this.hasEmptyFields() && this.hasEmptySuggestions()) || this.rangeFieldListener.shouldDisable();
        if (state) {
            this.elements.submitButton.setAttribute('disabled', `${state}`);
        } else {
            this.elements.submitButton.removeAttribute('disabled');
        }
    }

    /**
     * to prevent duplicate selection happened when clicked on the radio inputs at advanced filter section 
     */
     selectAdvancedFiltersRadioInput(event?:CustomEvent) {
        const radioInputs = this.domUtils.getElements<HTMLInputElement>(this.selectors.advancedFiltersRadioInputs);
        radioInputs.forEach(item => {
            item.addEventListener('change', (event)=>{
                radioInputs.forEach(item => {
                    item.checked = false;
                })
                const selectedTarget = event.target as HTMLInputElement;
                selectedTarget.checked = true;
            });
        });
    }

    /**
     * check if any of the text fields are not empty
     *
     * @return {boolean}
     */
    protected hasEmptyFields(): boolean {
        const textInputs = this.domUtils.getElements<HTMLInputElement>(this.selectors.searchInInputs, this.elements.searchInFields);
        const emptyFields = textInputs.filter((i) => {
            return !i.value;
        });
        return emptyFields.length === textInputs.length;
    }

    /**
     * check if all suggestions are empty
     *
     * @return {boolean}
     */
     protected hasEmptySuggestions(): boolean {
        if (!this.autoCompleteListener) {
            return true;
        }
        const suggestions = this.autoCompleteListener.autoCompletes;
        if (!suggestions.length) {
            return true;
        }
        return suggestions.filter(suggestion =>
            suggestion.getValues().length > 0
        ).length === 0;
    }

    protected setResetButtonListener() {
        this.wrapper.addEventListener('reset', (e) => {
            e.preventDefault();
            this.tabularListener.elements.searchInFields.forEach((item, k) => {
                if (k === 0) {
                    this.domUtils.getElement<HTMLInputElement>('input', item).value = '';
                    this.domUtils.getElement<HTMLSelectElement>('select', item).selectedIndex = 0;
                } else {
                    item.remove();
                }
            });
            this.tabularListener.toggleButtons();
            this.rangeFieldListener.elements.radios[0]?.click();
            this.autoCompleteListener.autoCompletes.forEach(item => {
                item.removeAllTags();
            });
        });
    }

}

export default AdvancedSearch;

export {
    Elements as AdvancedSearchElements,
    Selectors as AdvancedSearchSelectors,
    ClassList as AdvancedSearchClassList,
    Texts as AdvancedSearchTexts
}

